<template>
  <div>
    <div :class="['image-container', { 'custom-error-border': validator && validator.$error }]">
      <drop @drop="handleDrop">
        <div class="text-center p-2">
          <v-layout
            v-if="!value"
            justify-center
            align-center
            row
            class="image-placeholder"
            @click.stop.prevent="openFSPicker()"
          >
            <v-flex v-if="uploadInProgress">
              <v-progress-circular color="primary" indeterminate :size="75" />
            </v-flex>
            <v-flex v-else>
              <v-layout row align-center>
                <v-icon color="primary" class="icon-placeholder" large>photo</v-icon>
                <span class="text-description">{{ adminText(&quot;shellAll.recommended_image_size_1_062_x_96_px_maximum_file_size_10mb&quot;, &quot;Recommended image size: 1.062 x 96 px, maximum file size: 10MB.&quot;) }}</span>
              </v-layout>
            </v-flex>
          </v-layout>

          <v-layout
            v-else
            :style="{ backgroundImage: 'url(' + value + ')' }"
            justify-start
            align-end
            class="image-field"
          >
            <v-flex shrink class="ml-12 my-auto image-button">
              <main-button depressed style="margin: 0" @click.stop.prevent="openFSPicker">
                <v-icon>photo_library</v-icon>
              </main-button>
            </v-flex>
            <v-spacer />
            <v-flex
              v-if="!disableDelete"
              shrink
              class="my-auto image-button"
              style="margin-right: 12px"
            >
              <main-button outline color="error" class="remove-cover-btn" @click.stop="removeImage">
                <v-icon>delete_outline</v-icon>
              </main-button>
            </v-flex>
          </v-layout>
        </div>
      </drop>
    </div>
    <div :class="['custom-error-message', { 'error--text': validator && validator.$error }]">
      <span> {{ getErrors(validator)[0] }} &nbsp;</span>
    </div>
    <cropper-dialog
      :aspect-ratio="177 / 16"
      :image="dataURLImage"
      @dismissed="removeImage"
      @cropped="uploadAvatar($event)"
    />
    <hidden-file-input ref="file-input" max-file-size-mb="10" @file="processFile($event)" />
  </div>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import CropperDialog from './../dialogs/cropper-dialog'
import HiddenFileInput from './../hidden-file-input'
import { defaultMessages, fileToDataUrl, getUploadOptions, getValidationErrors, uploadImage } from '@/utils'
import { UploadPaths } from '@/types'

export default {
  mixins: [AdminContext],
  name: 'cover-field-horizontal',
  components: {
    CropperDialog,
    HiddenFileInput,
  },
  props: {
    validator: {
      type: Object,
      default: null,
    },
    uploadOptions: {
      type: Object,
      default: null,
    },
    errorMessages: {
      type: Object,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    disableDelete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dataURLImage: null,
      uploadInProgress: false,
    }
  },
  methods: {
    removeImage() {
      this.dataURLImage = null
      this.$emit('input', null)
      this.validator && this.validator.$touch()
    },
    handleDrop(data, event) {
      event.preventDefault()
      const {
        dataTransfer: { files },
      } = event
      this.processFile(files[0])
    },
    async processFile(file) {
      if (this.dataURLImage) {
        this.removeImage()
      }
      this.dataURLImage = await fileToDataUrl(file)
    },
    getErrors: function(field) {
      return getValidationErrors({
        ...this.adminValidationMessages(defaultMessages),
        ...this.adminFieldValidationMessages(this.errorMessages),
      })(field)
    },
    async uploadAvatar(dataURL) {
      this.uploadInProgress = true
      this.$emit('upload-in-progress', this.uploadInProgress)
      const uploadPath =
        this.uploadOptions && this.uploadOptions.path
          ? this.uploadOptions.path
          : UploadPaths.UNGROUPED
      const { file, path } = await getUploadOptions(dataURL, uploadPath, {
        width: 1062,
        height: 96,
      })
      const url = await uploadImage(file, path)
      this.$emit('input', url)
      this.uploadInProgress = false
      this.$emit('upload-in-progress', this.uploadInProgress)
      this.validator && this.validator.$touch()
    },
  },
}
</script>

<style scoped>
.pr-8 {
  padding-right: 8px;
}

.image-field {
  background-size: cover;
  width: 100%;
  border-radius: 8px;
  position: relative;
  border-radius: 8px;
  border: 1px solid var(--Stroke-One, rgba(151, 151, 151, 0.5));
}

.image-field::before {
  content: '';
  display: block;
  padding-top: 11.0625%;
}

.image-container {
  padding: 0;
  border-radius: 8px;
  background-color: #f5f5f5;
}

.image-placeholder:hover {
  background-color: rgba(2, 119, 189, 0.2) !important;
}

.icon-placeholder {
  color: #0277bd;
  margin: 0 0.75rem;
}

.text-placeholder {
  font-family: Raleway, sans-serif;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.33;
  text-align: center;
  color: #0277bd;
  margin: 8px 0 4px;
}

.image-placeholder {
  width: 100%;
  border: 2px dashed #cee4f2;
  border-radius: 8px;
  color: #fff;
  background-color: white;
  font-size: 14px;
  cursor: pointer;
}

.image-placeholder::before {
  content: '';
  display: block;
  padding-top: 11.0625%;
}

.text-description {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  line-height: 1.33;
  text-align: center;
  color: #0277bd;
}

.image-button {
  position: relative;
  z-index: 1;
}

.image-button >>> .v-icon {
  font-size: 20px;
}

.remove-cover-btn {
  margin: 0;
  background-color: white !important;
  border-width: 1.5px;
}

.image-button button {
  width: 37px !important;
  min-width: 0 !important;
}

.image-button .v-icon {
  margin-right: 0 !important;
}
</style>
